import React, { useState } from 'react';
import logo from '../kloudynet-logo.jpeg';
import TenantSwitcher from '../auth/TenantSwitcher';
import { useCurrentUser } from '../auth/useCurrentUser';
import { SignOutButton } from '../auth/SignInButton';

function getStackLabel(engagement) {
  if (!engagement) return null;
  const identity = engagement.identityPlatforms || [];
  const ai = engagement.aiWorkloads || [];
  const hasMS = identity.includes('microsoft365') || ai.includes('azure_openai');
  if (hasMS) return { short: '14 controls auto-scored' };
  return { short: 'No automation available' };
}

const ROLE_LABELS = {
  admin: 'Admin', assessor: 'Assessor', presales: 'Pre-sales', csp: 'CSP', client: 'Client',
};
const ROLE_COLORS = {
  admin: '#455A64', assessor: '#1565C0', presales: '#E65100', csp: '#0078D4', client: '#2E7D32',
};

export default function Sidebar({
  page, setPage, overview, engagement, role, isPlatformAdmin, allowedPages,
  onNewEngagement, onReset, onStartRecertification,
}) {
  const [showResetConfirm, setShowResetConfirm] = useState(false);
  const { displayName, email } = useCurrentUser();

  const scored = overview?.scored_controls || 0;
  const total = overview?.total_controls || 55;
  const pct = Math.round((scored / total) * 100);
  const score = overview?.overall_score || 0;
  const hasEng = !!engagement;
  const stackInfo = getStackLabel(engagement);
  const allowed = allowedPages || new Set();
  const isAssessor = role === 'assessor' || role === 'admin';

  const nav = (id, label, dot, disabled) => {
    if (!allowed.has(id)) return null;
    return (
      <div className={`nav-item${page === id ? ' active' : ''}${disabled ? ' nav-disabled' : ''}`}
        onClick={() => !disabled && setPage(id)}
        style={disabled ? { opacity: 0.32, cursor: 'not-allowed' } : {}}>
        <div className="nav-dot" style={{ background: dot }} />
        {label}
      </div>
    );
  };

  const isRecert = (engagement?.engType || '').toLowerCase().includes('recert');
  const workflowItems = ['auto', 'uploads', 'interviews', 'manual', 'evidence', 'report'].filter(id => allowed.has(id));
  const dashboardVisible = allowed.has('dashboard');

  return (
    <div className="sidebar">

      {/* Logo */}
      <div className="sidebar-logo">
        <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 8 }}>
          <img src={logo} alt="Kloudynet" style={{ width: 32, height: 32, borderRadius: 4, objectFit: 'contain' }} />
          <div>
            <div className="logo-brand">SAARA Portal</div>
            <div className="logo-sub">by Kloudynet</div>
          </div>
        </div>
        {hasEng
          ? <div className="logo-client">{engagement.orgName}</div>
          : <div className="logo-client" style={{ color: 'rgba(255,255,255,0.3)' }}>No active engagement</div>}
      </div>

      {/* Signed-in user + role badge */}
      <div style={{ padding: '10px 16px', borderBottom: '1px solid rgba(255,255,255,0.08)' }}>
        <div style={{ fontSize: 11, color: '#fff', fontWeight: 500, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
          {displayName || email || 'Signed in'}
        </div>
        {role && (
          <div style={{ display: 'flex', alignItems: 'center', gap: 6, marginTop: 4 }}>
            <div style={{ width: 8, height: 8, borderRadius: '50%', background: ROLE_COLORS[role] || '#fff', flexShrink: 0 }} />
            <span style={{ fontSize: 9, color: 'rgba(255,255,255,0.5)' }}>{ROLE_LABELS[role] || role}</span>
            {isPlatformAdmin && (
              <span style={{ fontSize: 8, padding: '1px 6px', borderRadius: 6, background: 'rgba(3,175,255,0.15)', color: '#03AFFF' }}>
                Platform admin
              </span>
            )}
          </div>
        )}
      </div>

      {/* Tenant switcher (only renders when user has > 1 assignment) */}
      <TenantSwitcher />

      {/* Active engagement strip */}
      {hasEng && (
        <div style={{ padding: '10px 16px', borderBottom: '1px solid rgba(255,255,255,0.08)' }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 6, marginBottom: 3 }}>
            <div style={{ width: 6, height: 6, borderRadius: '50%', background: '#4CAF50', flexShrink: 0 }} />
            <span style={{ fontSize: 10, color: 'rgba(255,255,255,0.45)' }}>Active engagement</span>
          </div>
          <div style={{ fontSize: 11, color: 'rgba(255,255,255,0.7)', lineHeight: 1.4, marginBottom: isRecert ? 4 : 0 }}>{engagement.engName}</div>
          {isRecert && <span style={{ display: 'inline-block', fontSize: 9, padding: '2px 7px', borderRadius: 8, background: 'rgba(3,175,255,0.15)', color: '#03AFFF' }}>{engagement.engType}</span>}
          {stackInfo && <div style={{ fontSize: 9, color: 'rgba(255,255,255,0.3)', marginTop: 3 }}>{stackInfo.short}</div>}
        </div>
      )}

      {/* Home */}
      <div className={`nav-item${page === 'home' ? ' active' : ''}`} onClick={() => setPage('home')} style={{ marginTop: 4 }}>
        <div className="nav-dot" style={{ background: '#fff' }} />
        Home
      </div>

      {/* Engagement — only assessor can create */}
      {isAssessor && allowed.has('initiate') && (
        <>
          <div className="nav-section">Engagement</div>
          <div className={`nav-item${page === 'initiate' ? ' active' : ''}`} onClick={onNewEngagement}>
            <div className="nav-dot" style={{ background: '#03AFFF' }} />
            New assessment
          </div>
        </>
      )}

      {/* Assessment workflow */}
      {workflowItems.length > 0 && (
        <>
          <div className="nav-section">{isAssessor ? 'Assessment workflow' : 'Assessment'}</div>
          {nav('auto',       '1. Auto-scored controls',  '#4CAF50', !hasEng)}
          {nav('uploads',    '2. Document uploads',       '#6A1B9A', !hasEng)}
          {nav('interviews', '3. Stakeholder interviews', '#455A64', !hasEng)}
          {nav('manual',     '4. Manual inputs',          '#F57F17', !hasEng)}
          {nav('evidence',   '5. Evidence review',        '#FFA000', !hasEng)}
          {nav('report',     isAssessor ? '6. Findings report' : 'Findings report', '#1565C0', !hasEng)}
        </>
      )}

      {dashboardVisible && (
        <>
          <div className="nav-section">Dashboard</div>
          {nav('dashboard', 'Assessment dashboard', '#03AFFF', !hasEng)}
        </>
      )}

      <div className="nav-section">Reference</div>
      {nav('framework', 'SAARA Framework', '#6A1B9A', false)}

      {(allowed.has('toolkit') || allowed.has('toolkit-admin')) && (
        <>
          <div className="nav-section">Toolkit</div>
          {nav('toolkit', 'Toolkit library', '#00796B', false)}
          {nav('toolkit-admin', 'Toolkit administration', '#E65100', false)}
        </>
      )}

      {/* Administration */}
      <div className="nav-section">Administration</div>
      <div className={`nav-item${page === 'profile' ? ' active' : ''}`} onClick={() => setPage('profile')}>
        <div className="nav-dot" style={{ background: '#7A8899' }} />
        Profile
      </div>
      {allowed.has('user-management') && (
        <div className={`nav-item${page === 'user-management' ? ' active' : ''}`} onClick={() => setPage('user-management')}>
          <div className="nav-dot" style={{ background: '#1565C0' }} />
          User management
        </div>
      )}
      {isPlatformAdmin && (
        <div className={`nav-item${page === 'internal-pool' ? ' active' : ''}`} onClick={() => setPage('internal-pool')}>
          <div className="nav-dot" style={{ background: '#03AFFF' }} />
          Internal pool
        </div>
      )}
      {isPlatformAdmin && (
        <div className={`nav-item${page === 'tenants' ? ' active' : ''}`} onClick={() => setPage('tenants')}>
          <div className="nav-dot" style={{ background: '#6A1B9A' }} />
          Tenants
        </div>
      )}

      {/* Progress + reset */}
      <div className="sidebar-progress">
        {hasEng && isAssessor ? (
          <>
            <div className="progress-label">
              <span>Controls scored</span>
              <span style={{ color: 'rgba(255,255,255,0.6)' }}>{scored}/{total}</span>
            </div>
            <div className="progress-track">
              <div className="progress-fill" style={{ width: `${pct}%` }} />
            </div>
            {score > 0 && <div className="score-badge">Overall maturity: {score} / 5.0</div>}

            {!showResetConfirm ? (
              <button onClick={() => setShowResetConfirm(true)} style={{
                marginTop: 12, width: '100%', padding: '6px 0', fontSize: 10, fontWeight: 500,
                background: 'transparent', border: '1px solid rgba(198,40,40,0.4)', borderRadius: 6,
                color: 'rgba(255,100,100,0.7)', cursor: 'pointer',
              }}>
                Delete engagement
              </button>
            ) : (
              <div style={{ marginTop: 10, padding: '10px', background: 'rgba(198,40,40,0.15)', borderRadius: 6, border: '1px solid rgba(198,40,40,0.4)' }}>
                <div style={{ fontSize: 10, color: '#FF9999', marginBottom: 8, lineHeight: 1.4 }}>
                  This will <strong>permanently delete</strong> the engagement, all scores, and all assessment runs.
                </div>
                <div style={{ display: 'flex', gap: 6 }}>
                  <button onClick={() => { setShowResetConfirm(false); onReset(); }} style={{ flex: 1, padding: '5px 0', fontSize: 10, fontWeight: 600, background: '#C62828', border: 'none', borderRadius: 4, color: '#fff', cursor: 'pointer' }}>
                    Delete permanently
                  </button>
                  <button onClick={() => setShowResetConfirm(false)} style={{ flex: 1, padding: '5px 0', fontSize: 10, background: 'transparent', border: '1px solid rgba(255,255,255,0.2)', borderRadius: 4, color: 'rgba(255,255,255,0.6)', cursor: 'pointer' }}>
                    Cancel
                  </button>
                </div>
              </div>
            )}
          </>
        ) : hasEng ? (
          <div style={{ fontSize: 10, color: 'rgba(255,255,255,0.35)', textAlign: 'center', lineHeight: 1.6 }}>
            {scored}/{total} controls scored<br />
            {score > 0 && `Maturity: ${score} / 5.0`}
          </div>
        ) : (
          <div style={{ fontSize: 10, color: 'rgba(255,255,255,0.25)', textAlign: 'center', lineHeight: 1.6 }}>
            {isAssessor ? 'Start an engagement to\nbegin the assessment' : 'No active engagement'}
          </div>
        )}

        <SignOutButton style={{
          marginTop: 14, width: '100%', padding: '6px 0', fontSize: 10,
          background: 'transparent', border: '1px solid rgba(255,255,255,0.15)',
          borderRadius: 6, color: 'rgba(255,255,255,0.6)', cursor: 'pointer',
        }} />
      </div>
    </div>
  );
}
